import debounce from "lodash.debounce";
import { mdiClose, mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import {
  KeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface AsyncSearchSelectProps<T> {
  value?: T | null;
  onSelect: (value: T) => void;
  /** Caller-supplied data source. Should resolve to [] on failure. */
  fetcher: (query: string) => Promise<T[]>;
  /** Text shown in the input for the selected value + typed query baseline. */
  getOptionLabel: (item: T) => string;
  /** Rich dropdown row; falls back to getOptionLabel. */
  renderOption?: (item: T) => ReactNode;
  getOptionKey?: (item: T) => string;
  disabled?: boolean;
  placeholder?: string;
  debounceMs?: number;
  minChars?: number;
  size?: "sm" | "md";
  allowClear?: boolean;
  onClear?: () => void;
  onAddNew?: () => void;
  className?: string;
  clearOnSelect?: boolean;
}

/**
 * Generic async autocomplete: an always-editable compact input backed by a
 * caller-supplied fetcher. Entity-agnostic and reusable app-wide — wrap it with
 * an entity fetcher + label (see entity_selects/) or use it inline.
 */
function AsyncSearchSelect<T>({
  value,
  onSelect,
  fetcher,
  getOptionLabel,
  renderOption,
  getOptionKey,
  disabled = false,
  placeholder = "Search",
  debounceMs = 500,
  minChars = 1,
  size = "sm",
  allowClear = false,
  onClear,
  onAddNew,
  className,
  clearOnSelect = false,
}: AsyncSearchSelectProps<T>): JSX.Element {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<T[]>();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const rootRef = useRef<HTMLDivElement>(null);
  const reqId = useRef(0);
  const fetcherRef = useRef(fetcher);
  const labelRef = useRef(getOptionLabel);
  fetcherRef.current = fetcher;
  labelRef.current = getOptionLabel;

  const valueKey = value
    ? (getOptionKey?.(value) ?? labelRef.current(value))
    : "";

  // Reflect the selected value in the input only when the value itself changes,
  // never on unrelated re-renders — otherwise typing would be overwritten.
  useEffect(() => {
    setQuery(value ? labelRef.current(value) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const fetchNow = (q: string) => {
    reqId.current += 1;
    const id = reqId.current;
    setLoading(true);
    fetcherRef
      .current(q)
      .then((res) => {
        if (id === reqId.current) setOptions(res);
      })
      .catch(() => {
        if (id === reqId.current) setOptions([]);
      })
      .finally(() => {
        if (id === reqId.current) setLoading(false);
      });
  };
  const fetchRef = useRef(fetchNow);
  fetchRef.current = fetchNow;

  const debounced = useMemo(
    () => debounce((q: string) => fetchRef.current(q), debounceMs),
    [debounceMs],
  );

  useEffect(() => () => debounced.cancel(), [debounced]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const onInput = (v: string) => {
    setQuery(v);
    setOpen(true);
    setFocusedIndex(-1);
    if (v.length >= minChars) {
      debounced(v);
    } else {
      setOptions(undefined);
    }
  };

  const select = (item: T) => {
    onSelect(item);
    setQuery(clearOnSelect ? "" : labelRef.current(item));
    setOpen(false);
    setOptions(undefined);
    setFocusedIndex(-1);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((i) => Math.min(i + 1, (options?.length ?? 0) - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      // Never let Enter bubble to a surrounding form while searching.
      e.preventDefault();
      if (options && focusedIndex >= 0) select(options[focusedIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={`relative w-full ${className ?? ""}`}>
      <div className="flex items-center border rounded-md w-full bg-white">
        <input
          disabled={disabled}
          autoComplete="off"
          value={query}
          placeholder={placeholder}
          onChange={(e) => onInput(e.target.value)}
          onFocus={() => {
            // minChars 0 means "browsable list": show every option on focus
            // (re-fetched, since the caller's source may have changed).
            if (minChars === 0) {
              setOpen(true);
              fetchNow("");
              return;
            }
            if (options && options.length) setOpen(true);
          }}
          onKeyDown={onKeyDown}
          className={`w-full text-xs px-2 rounded-md focus:outline-none bg-transparent ${
            size === "md" ? "h-9" : "h-8"
          }`}
          type="text"
        />
        {allowClear && onClear && query && !disabled && (
          <button
            type="button"
            onClick={() => {
              onClear();
              setQuery("");
              setOptions(undefined);
            }}
            className="px-1 text-gray-400 hover:text-gray-600"
          >
            <Icon path={mdiClose} className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && (options || loading) && (
        <div className="absolute z-40 mt-1 w-full border rounded bg-white shadow-lg max-h-60 overflow-y-auto divide-y">
          {options?.map((opt, i) => (
            <div
              key={getOptionKey?.(opt) ?? i}
              onMouseDown={(e) => {
                e.preventDefault();
                select(opt);
              }}
              className={`text-xs px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                i === focusedIndex ? "bg-gray-100" : ""
              }`}
            >
              {renderOption ? renderOption(opt) : getOptionLabel(opt)}
            </div>
          ))}
          {loading && (
            <div className="text-xs px-3 py-2 text-gray-400">Loading…</div>
          )}
          {!loading && options && options.length === 0 && (
            <div className="text-xs px-3 py-2 text-gray-400 flex justify-between items-center">
              <span>No results</span>
              {onAddNew && (
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onAddNew();
                  }}
                  className="flex items-center gap-1 text-myPrimaryColor"
                >
                  <Icon path={mdiPlus} className="h-4 w-4" /> Add new
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AsyncSearchSelect;
